import * as CANNON from 'cannon-es';

export class PhysicsWorld {
  constructor(game) {
    // Store reference to game
    this.game = game;
    
    // Create world with gravity
    this.world = new CANNON.World();
    this.world.gravity.set(0, -6.5, 0); // Updated to match Rocket League (650uu/s²)
    
    // Set solver iterations for more stable physics
    this.world.solver.iterations = 20; // Increased iterations
    this.world.defaultContactMaterial.contactEquationStiffness = 1e7;
    this.world.defaultContactMaterial.contactEquationRelaxation = 4;
    
    // Create materials for better physical interactions
    this.createMaterials();
    
    // Debug
    this.debug = true;
    if (this.debug) {
      console.log('Physics world created with Rocket League gravity (-6.5)');
    }
  }
  
  createMaterials() {
    // Car material
    this.carMaterial = new CANNON.Material('car');
    
    // Ground material
    this.groundMaterial = new CANNON.Material('ground');
    
    // Ball material
    this.ballMaterial = new CANNON.Material('ball');
    
    // Wall material
    this.wallMaterial = new CANNON.Material('wall');
    
    // Car-ground contact material (improved traction and stability)
    const carGroundContactMaterial = new CANNON.ContactMaterial(
      this.carMaterial,
      this.groundMaterial,
      {
        friction: 0.7,             // Increased friction for better grip (was 0.5)
        restitution: 0.1,          // Reduced restitution to prevent bouncing (was 0.2)
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 4,  // Increased for more gradual collision response
        frictionEquationStiffness: 1e8
      }
    );
    this.world.addContactMaterial(carGroundContactMaterial);
    
    // Ball-ground contact material (bouncy)
    const ballGroundContactMaterial = new CANNON.ContactMaterial(
      this.ballMaterial,
      this.groundMaterial,
      {
        friction: 0.3,             // Increased friction
        restitution: 0.5,         // Reduced bounciness
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 4,
        frictionEquationStiffness: 1e8
      }
    );
    this.world.addContactMaterial(ballGroundContactMaterial);
    
    // Car-ball contact material (improved for better ball control)
    const carBallContactMaterial = new CANNON.ContactMaterial(
      this.carMaterial,
      this.ballMaterial,
      {
        friction: 0.5,             // Increased friction for better control
        restitution: 0.3,          // Reduced bounciness for more predictable hits
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3,
        frictionEquationStiffness: 1e8
      }
    );
    this.world.addContactMaterial(carBallContactMaterial);
    
    // Ball-wall contact material (very bouncy)
    const ballWallContactMaterial = new CANNON.ContactMaterial(
      this.ballMaterial,
      this.wallMaterial,
      {
        friction: 0.15,
        restitution: 0.85,
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3,
        collisionResponse: true
      }
    );
    this.world.addContactMaterial(ballWallContactMaterial);
    
    // Car-wall contact material (improved to prevent sticking)
    const carWallContactMaterial = new CANNON.ContactMaterial(
      this.carMaterial,
      this.wallMaterial,
      {
        friction: 0.2,             // Reduced friction to prevent wall sticking (was 0.3)
        restitution: 0.3,          // Adjusted restitution
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 5,  // More relaxation for smoother wall contacts
        collisionResponse: true
      }
    );
    this.world.addContactMaterial(carWallContactMaterial);
    
    // Wall-wall contact material (for completeness)
    const wallWallContactMaterial = new CANNON.ContactMaterial(
      this.wallMaterial,
      this.wallMaterial,
      {
        friction: 0.2,
        restitution: 0.3,
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3
      }
    );
    this.world.addContactMaterial(wallWallContactMaterial);
    
    // Ground-wall contact material
    const groundWallContactMaterial = new CANNON.ContactMaterial(
      this.groundMaterial,
      this.wallMaterial,
      {
        friction: 0.2,
        restitution: 0.3,
        contactEquationStiffness: 1e8,
        contactEquationRelaxation: 3
      }
    );
    this.world.addContactMaterial(groundWallContactMaterial);
  }
  
  addBody(body) {
    // Ensure all bodies have collision response enabled by default
    body.collisionResponse = true;
    this.world.addBody(body);
  }
  
  removeBody(body) {
    this.world.removeBody(body);
  }
  
  cleanup() {
    // Remove all bodies from the physics world
    while(this.world.bodies.length > 0) {
      this.world.removeBody(this.world.bodies[0]);
    }
    
    // Clear all constraints
    while(this.world.constraints.length > 0) {
      this.world.removeConstraint(this.world.constraints[0]);
    }
    
    // Clear contact materials
    this.world.contactmaterials.length = 0;
    
    // Clear references
    this.carMaterial = null;
    this.groundMaterial = null;
    this.ballMaterial = null;
    this.wallMaterial = null;
    this.game = null;
  }
  
  update(delta) {
    // CRITICAL FIX: Force fixed timestep at all times
    // This ensures consistent physics simulation even with variable framerate
    const fixedTimeStep = 1/60; // Always use 60Hz physics
    
    // NEVER sleep any bodies - wake everything every frame
    this.world.bodies.forEach(body => {
      body.allowSleep = false; // Disable sleeping
      body.sleepState = 0; // Force awake state
      body.wakeUp();
      
      // Force all dynamic bodies to stay dynamic
      if (body.mass > 0) {
        body.type = CANNON.Body.DYNAMIC;
      }
    });
    
    // Step the world with fixed timestep for stability
    // Use multiple substeps for more accurate collision
    this.world.step(fixedTimeStep);
    
    // Force an additional micro-step to ensure everything is processing
    this.world.step(0.001);
  }
  
  createBoxBody(dimensions, position, options = {}) {
    const { mass = 0, material = this.groundMaterial } = options;
    
    const halfExtents = new CANNON.Vec3(
      dimensions.x / 2,
      dimensions.y / 2,
      dimensions.z / 2
    );
    
    const boxShape = new CANNON.Box(halfExtents);
    const boxBody = new CANNON.Body({
      mass,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      shape: boxShape,
      material,
      collisionResponse: true, // Ensure collision response is enabled
      collisionFilterGroup: 1, // Default collision group
      collisionFilterMask: 255 // Collide with everything
    });
    
    this.addBody(boxBody);
    return boxBody;
  }
  
  createSphereBody(radius, position, options = {}) {
    const { mass = 1, material = this.ballMaterial } = options;
    
    const sphereShape = new CANNON.Sphere(radius);
    const sphereBody = new CANNON.Body({
      mass,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      shape: sphereShape,
      material,
      linearDamping: 0.1,
      angularDamping: 0.1,
      allowSleep: true,
      sleepSpeedLimit: 0.1,
      sleepTimeLimit: 1,
      collisionResponse: true, // Ensure collision response is enabled
      collisionFilterGroup: 1, // Default collision group
      collisionFilterMask: 255 // Collide with everything
    });
    
    this.addBody(sphereBody);
    return sphereBody;
  }
} 